import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAuthStore } from '@/store/auth'
import { useUIStore } from '@/store/ui'
import { useEffectiveClientId } from '@/hooks/useAuth'
import type { TopicWeight } from '@/types/database'

export function useTopicWeights() {
  const clientId = useEffectiveClientId()
  return useQuery({
    queryKey: ['topic-weights', clientId],
    enabled: !!clientId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('topic_weights')
        .select('*')
        .eq('client_id', clientId!)
      if (error) throw error
      return data as TopicWeight[]
    },
  })
}

export function useUpdateTopicWeights() {
  const queryClient = useQueryClient()
  const clientId = useEffectiveClientId()

  return useMutation({
    mutationFn: async (weights: { topic: string; weight: number }[]) => {
      const rows = weights.map((w) => ({ client_id: clientId!, topic: w.topic, weight: w.weight }))
      // topic_weights has unique(client_id, topic); without onConflict the upsert
      // targets the primary key and duplicates instead of updating.
      const { error } = await supabase
        .from('topic_weights')
        .upsert(rows, { onConflict: 'client_id,topic' })
      if (error) throw error
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['topic-weights', clientId] })
    },
  })
}

export function useUpdateAccentColor() {
  const queryClient = useQueryClient()
  const clientId = useEffectiveClientId()

  return useMutation({
    mutationFn: async (accentColor: string) => {
      if (!clientId) throw new Error('No client selected')
      const { error } = await supabase
        .from('clients')
        .update({ accent_color: accentColor })
        .eq('id', clientId)
      if (error) throw error
      return accentColor
    },
    onSuccess: (accentColor) => {
      // The client lives in Zustand, not React Query — update whichever copy the
      // UI is actually rendering (impersonated tenant, or the user's own client).
      const { viewingAs, setViewingAs } = useUIStore.getState()
      if (viewingAs?.id === clientId) {
        setViewingAs({ ...viewingAs, accent_color: accentColor })
      } else {
        const { client, setClient } = useAuthStore.getState()
        if (client?.id === clientId) setClient({ ...client, accent_color: accentColor })
      }
      void queryClient.invalidateQueries({ queryKey: ['admin', 'client', clientId] })
    },
  })
}
